import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/Badge";
import { Card } from "@/components/ui/Card";
import { PriceTag } from "@/components/ui/PriceTag";
import type { ProductListItem } from "@/lib/api-client";

export interface ProductCardProps {
  product: ProductListItem;
}

/**
 * `CarteProduit` — porté depuis apps/web/components/product/ProductCard.tsx.
 * Seul changement : `next/link` → `Link` de react-router-dom (`href` → `to`).
 */
export function ProductCard({ product }: ProductCardProps) {
  // Jamais une vidéo en vignette (rendue en <img>) : images uniquement.
  const images = product.media.filter((m) => m.type === "IMAGE");
  const primaryImage = images.find((m) => m.isPrimary) ?? images[0] ?? null;
  const hasPriceRange = product.priceMin.amountMinor !== product.priceMax.amountMinor;

  const badges: Array<{ label: string; tone: "promo" | "new" | "outOfStock" }> = [];
  if (!product.inStock) badges.push({ label: "Rupture", tone: "outOfStock" });
  if (product.onSale) badges.push({ label: "Promo", tone: "promo" });
  if (product.tags.some((t) => t.code === "new" || t.code === "nouveau")) {
    badges.push({ label: "Nouveau", tone: "new" });
  }

  return (
    <Card className="group flex h-full flex-col overflow-hidden">
      <Link to={`/p/${product.slug}`} className="flex h-full flex-col">
        <div className="relative aspect-square w-full overflow-hidden bg-cream2">
          {badges.length > 0 ? (
            <div className="absolute left-2 top-2 z-10 flex flex-col gap-1">
              {badges.map((b) => (
                <Badge key={b.label} tone={b.tone}>
                  {b.label}
                </Badge>
              ))}
            </div>
          ) : null}

          {primaryImage ? (
            <img
              src={primaryImage.url}
              alt={primaryImage.alt ?? product.name}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-4 text-center">
              <span aria-hidden="true" className="text-3xl text-line">
                {"\u{1F4E6}"}
              </span>
              <span className="line-clamp-2 font-body text-xs text-muted">{product.name}</span>
            </div>
          )}
        </div>

        <div className="flex flex-1 flex-col gap-1 p-3">
          {product.brand ? (
            <span className="font-body text-[11px] font-semibold uppercase tracking-wide text-muted">
              {product.brand.name}
            </span>
          ) : null}

          <h3 className="line-clamp-2 font-body text-sm font-medium text-ink">{product.name}</h3>

          {product.rating.count > 0 ? (
            <p className="font-body text-xs text-muted">
              <span aria-hidden="true" className="text-gold">
                {"★".repeat(Math.round(product.rating.average))}
                {"☆".repeat(5 - Math.round(product.rating.average))}
              </span>{" "}
              ({product.rating.count})
            </p>
          ) : null}

          <div className="mt-auto pt-1">
            <PriceTag
              amountMinor={product.priceMin.amountMinor}
              compareAtAmountMinor={product.compareAtPrice?.amountMinor ?? null}
            />
            {hasPriceRange ? (
              <p className="font-body text-xs text-muted">plusieurs déclinaisons</p>
            ) : null}
          </div>
        </div>
      </Link>
    </Card>
  );
}
